import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { makeStyles, Typography, TextField, MenuItem, Grid } from '@material-ui/core';
import { appProfileSelector, loggedInSelector } from 'redux/selectors';
import { setLoading } from 'redux/actions/app';
import { useSelector, useDispatch } from 'react-redux';
import * as API from 'services/api';
import { isEmail } from 'utils/validations';
import PropTypes from 'prop-types';
import { showMessage } from 'utils';
import CommonModal from 'components/Modals/CommonModal';

const useStyles = makeStyles(theme => ({
  subtitle: {
    color: '#464F73'
  },
  label: {
    fontWeight: 'bold',
    color: theme.palette.app.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5)
  },
  modal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 800,
    maxHeight: '100%',
    overflowY: 'auto',
    borderRadius: 10,
    [theme.breakpoints.down('xs')]: {
      width: '93%',
    },
  },
}));
const CIVILITIES = [
  'madame',
  'monsieur',
  'sir',
  'madam',
]
const FUNCTIONS = [
  "achat_chain",
  "designoffice",
  "commercial",
  "communication",
  "dirigeant",
  "finances",
  "fonctionnaire",
  "asset",
  "it",
  "marketing",
  "QHSE",
  "RH",
  "services",
  "support",
]

const REQUESTTYPES = [
  'quote',
  'additional',
  'invoice',
  'other'
]

const COUNTRIES = [
  "France",
  "Angola",
  "Arabie Saoudite",
  "Belgique",
  "Bénin",
  "Birmanie",
  "Brunei",
  "Burkina Faso",
  "Cameroun",
  "Chine",
  "Corée du Sud",
  "Côte d Ivoire",
  "Croatie",
  "Danemark",
  "Émirats Arabes Unis",
  "Espagne",
  "Etats Unis",
  "Gabon",
  "Ghana",
  "Guinée Conakry",
  "Guinée Equatoriale",
  "Île Maurice",
  "Inde",
  "Indonésie",
  "Italie",
  "Japon",
  "Koweït",
  "Macédoine",
  "Madagascar",
  "Malaisie",
  "Mali",
  "Maroc",
  "Martinique",
  "Monaco",
  "Oman",
  "Pays-Bas",
  "Pologne",
  "Qatar",
  "Royaume-Uni",
  "Sénégal",
  "Serbie",
  "Singapour",
  "Tchad",
  "Thaïlande",
  "Tunisie",
  "Vietnam",
  "Autre",
];

const fields = [
  { field: "civility", values: CIVILITIES, transKey: "contact.field.civility." },
  { field: "firstName" },
  { field: "lastName" },
  { field: "email" },
  { field: "phone" },
  { field: "country", values: COUNTRIES },
  { field: "company" },
  { field: "function", values: FUNCTIONS, transKey: "contact.field.function." },
  { field: "activity", noRequired: true },
  { field: "requesttype", values: REQUESTTYPES, transKey: "contact.field.requesttype." }
]
const initialized = {
  civility: "",
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: "",
  company: '',
  function: '',
  activity: '',
  requesttype: '',
  description: ''
}

const buildSelectedProduct = (visible) => {
  if (!visible || typeof visible !== 'object') return null;
  if (visible.type === "fab") return null;

  const selectedProduct = {
    title: visible.title || null,
    refCode: visible.refCode || null,
    productId: visible.productId || null,
    catalogueId: visible.catalogueId || null,
    sessionId: visible.sessionId || null,
  };

  return Object.values(selectedProduct).some(value => value) ? selectedProduct : null;
};

const buildQuestionData = (selectedProduct, type) => {
  if (type !== "question" || !selectedProduct) return null;
  return JSON.stringify(selectedProduct);
};

const ContactModal = ({ intl: { formatMessage }, visible, onClose, appId }) => {
  const classes = useStyles();
  const profile = useSelector(appProfileSelector);
  const dispatch = useDispatch();
  const [user, setUser] = useState(initialized)
  const isLoggedIn = useSelector(loggedInSelector);

  useEffect(() => {
    if (visible) {
      setUser({
        ...initialized,
        ...profile ? {
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          phone: profile.phone,
          country: profile.country,
          company: profile.company,
        } : {}
      })
    } else {
      setUser(initialized)
    }
  }, [visible]);

  const onSubmitRequest = () => {
    dispatch(setLoading(true));
    const selectedProduct = buildSelectedProduct(visible);
    const questionData = buildQuestionData(selectedProduct, visible?.type);
    API.createRequest({
      appId,
      ...user,
      ...visible.type && visible.type != "fab" ? visible : {},
      ...selectedProduct ? { selectedProduct } : {},
      ...questionData ? { data: questionData } : {},
    }).then(res => {
      dispatch(setLoading(false));
      if (res.code == 201) {
        showMessage({
          message: 'Submitted successfully!',
          type: 'success',
        });
        onClose(true)
        return
      }
      showMessage({
        message: 'Failed!',
        type: 'warning',
      });
    }).catch(() => {
      showMessage({
        message: 'Failed!',
        type: 'warning',
      });
      dispatch(setLoading(false));
    });
  }

  const btnDisabled = isLoggedIn ? !user.description : (!isEmail(user.email) || !user.firstName || !user.lastName
    || !user.civility
    || !user.phone
    || !user.country
    || !user.company
    || !user.function
    || !user.requesttype
    || user.country == "France" && !user.postalCode
  );

  let title = formatMessage({ id: "applications.contact.title" });
  let description = formatMessage({ id: "applications.contact.subtitle" })

  if (visible?.type == "fab") {
    title = "VOUS AVEZ UNE QUESTION ";
  } else if (visible?.type) {
    description = <>Vous avez une question sur le produit <strong>"{visible.title}"</strong>, notre équipe est à votre disposition pour y répondre.</>
  }

  return (
    <CommonModal open={!!visible}
      onClose={onClose}
      title={title}
      buttonLabel={formatMessage({ id: "applications.contact.submit" })}
      btnDisabled={btnDisabled}
      onSubmit={onSubmitRequest}
      className= {classes.modal}
    >
      <Typography className={classes.subtitle}>{description}</Typography>
      {
        !isLoggedIn &&
          <Grid container spacing={1}>
            {
              fields.map(item =>
                <>
                  <Grid key={item.field} item xs={6}>
                    <Typography className={classes.label}>{formatMessage({ id: "contact.field." + item.field })}{!item.noRequired && <span style={{ color: '#DF7C70' }}>*</span>}</Typography>
                    {
                      item.values ?
                        <TextField
                          required
                          fullWidth
                          value={user[item.field]}
                          onChange={e => {
                            e.persist();
                            setUser(prev => ({
                              ...prev,
                              [item.field]: e.target.value,
                              ...item.field == "country" && e.target.value != "France" ? { postalCode: '', siret: '' } : {}
                            }))
                          }}
                          variant="outlined"
                          select
                          placeholder={formatMessage({ id: "selectplaceholder" })}
                        >
                          <MenuItem value="">{formatMessage({ id: "selectplaceholder" })}</MenuItem>
                          {
                            item.values.map(id => <MenuItem key={id} value={id}>{item.transKey ? formatMessage({ id: item.transKey + id }) : id}</MenuItem>)
                          }
                        </TextField> :
                        <TextField
                          required
                          fullWidth
                          value={user[item.field]}
                          onChange={e => {
                            e.persist();
                            setUser(prev => ({
                              ...prev,
                              [item.field]: e.target.value
                            }))
                          }}
                          variant="outlined"
                        />
                    }
                  </Grid>
                  {
                    item.field == "country" && user[item.field] == "France" &&
                    <>
                      <Grid key={item.field} item xs={6}>
                        <Typography className={classes.label}>Code postal<span style={{ color: '#DF7C70' }}>*</span></Typography>
                        <TextField
                          required
                          fullWidth
                          value={user["postalCode"]}
                          onChange={e => {
                            e.persist();
                            setUser(prev => ({
                              ...prev,
                              postalCode: e.target.value
                            }))
                          }}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid key={item.field} item xs={6}>
                        <Typography className={classes.label}>SIRET</Typography>
                        <TextField
                          required
                          fullWidth
                          value={user["siret"]}
                          onChange={e => {
                            e.persist();
                            setUser(prev => ({
                              ...prev,
                              siret: e.target.value
                            }))
                          }}
                          variant="outlined"
                        />
                      </Grid>
                    </>
                  }
                </>
              )
            }
          </Grid>
      }
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography className={classes.label}>{formatMessage({ id: "contact.field.description" })}</Typography>
          <TextField
            fullWidth
            value={user.description}
            onChange={e => {
              e.persist();
              setUser(prev => ({
                ...prev,
                description: e.target.value
              }))
            }}
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </CommonModal>
  );
};
ContactModal.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }),
  visible: PropTypes.any,
  onClose: PropTypes.func,
  appId: PropTypes.any
};
export default injectIntl(ContactModal);


